import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories } from '@/mocks/site-config';
import { latestArticles } from '@/mocks/home-data';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find(c => c.slug === slug);
  const categoryName = category?.name || '未知分类';
  const categoryDesc = category?.description || '浏览最新爆料内容';

  const filteredArticles = slug
    ? latestArticles.filter(a => a.category === slug)
    : latestArticles;

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'http://zzqpq.cn/' },
      { '@type': 'ListItem', position: 2, name: categoryName, item: `http://zzqpq.cn/category/${slug}` },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          <section className="relative w-full min-h-[320px] md:min-h-[420px] flex flex-col items-center justify-start pt-28 md:pt-36 pb-12 overflow-hidden">
            <div className="absolute inset-0">
              <img
                src="https://readdy.ai/api/search-image?query=Dark%20atmospheric%20abstract%20background%20with%20warm%20amber%20and%20deep%20brown%20gradient%2C%20mysterious%20moody%20texture%2C%20dramatic%20editorial%20style%2C%20no%20text%2C%20no%20people&width=1920&height=600&seq=category-hero-bg&orientation=landscape"
                alt={`${categoryName}分类背景`}
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/85" />

            <div className="relative z-10 w-full text-center px-4">
              <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-black text-background-50 leading-tight">
                {categoryName}
              </h1>
              <p className="mt-4 text-sm md:text-base text-background-50/60 max-w-lg mx-auto">
                {categoryDesc}
              </p>
            </div>

            <div className="relative z-10 w-full max-w-5xl mx-auto mt-10 px-4">
              <div className="flex flex-wrap items-center justify-center gap-3">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-label text-sm transition-all cursor-pointer whitespace-nowrap ${
                      cat.slug === slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-white/10 text-background-50/80 hover:bg-white/20 hover:text-background-50'
                    }`}
                  >
                    <i className={`${cat.icon} text-base`} />
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </section>

          <section className="py-8 md:py-10 bg-background-50">
            <div className="px-4 md:px-6">
              <nav className="flex items-center gap-2 text-sm text-foreground-400 mb-8" aria-label="面包屑导航">
                <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link>
                <i className="ri-arrow-right-s-line text-xs" />
                <span className="text-foreground-700 font-medium">{categoryName}</span>
              </nav>

              {filteredArticles.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                  {filteredArticles.map((article, index) => (
                    <Link
                      key={article.id}
                      to={`/article/${article.id}`}
                      className="rounded-xl bg-background-100 overflow-hidden cursor-pointer group transition-all duration-300 hover:-translate-y-1"
                    >
                      {index === 0 && (
                        <div className="absolute top-3 left-3 z-10">
                          <span className="px-2 py-0.5 bg-primary-500 text-background-50 text-[10px] font-semibold rounded-md whitespace-nowrap">
                            TOP
                          </span>
                        </div>
                      )}
                      <div className="p-3 md:p-4">
                        <div className="aspect-[4/3] rounded-lg overflow-hidden bg-background-200">
                          <img
                            src={article.image}
                            alt={article.title}
                            title={article.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                      </div>
                      <div className="px-3 md:px-4 pb-4">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-[10px] md:text-xs text-primary-500 font-medium">{article.categoryName}</span>
                          {article.isNew && (
                            <span className="px-1.5 py-0.5 bg-accent-100 text-accent-700 text-[10px] font-semibold rounded whitespace-nowrap">
                              NEW
                            </span>
                          )}
                        </div>
                        <h3 className="font-heading text-sm md:text-base font-bold text-foreground-950 leading-snug group-hover:text-primary-600 transition-colors line-clamp-2">
                          {article.title}
                        </h3>
                        <div className="flex items-center justify-between mt-3 text-xs text-foreground-400">
                          <span>{article.date}</span>
                          <span className="flex items-center gap-1">
                            <i className="ri-eye-line text-xs" />
                            {article.views > 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views.toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <i className="ri-inbox-line text-5xl text-foreground-300" />
                  <p className="mt-4 text-foreground-500">该分类下暂无内容</p>
                  <Link to="/" className="mt-4 inline-block text-primary-500 font-medium text-sm hover:underline cursor-pointer">
                    返回首页
                  </Link>
                </div>
              )}
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}